"use client";

import { useRef } from "react";
import {
  Brain,
  Network,
  Zap,
  ShieldCheck,
  Activity,
  GaugeCircle,
  LineChart,
  Users,
} from "lucide-react";
import FadeInCard from "./FadeInCard";
import FadeInOnScroll from "./FadeInOnScroll"; // 名字随便，但首字母要大写

// 通用交互动效卡片：白底 + 紫色高光
function InteractiveCard({ children, className = "" }) {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -10;
    const rotateY = ((x - centerX) / centerX) * 10;

    cardRef.current.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
    cardRef.current.style.transition = `transform 0.12s ease-out`;
    cardRef.current.style.boxShadow = `${-rotateY}px ${rotateX}px 26px rgba(168, 85, 247, 0.35)`;

    cardRef.current.style.setProperty("--x", `${x}px`);
    cardRef.current.style.setProperty("--y", `${y}px`);
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;

    cardRef.current.style.transform = `perspective(800px) rotateX(0deg) rotateY(0deg)`;
    cardRef.current.style.transition = `transform 0.35s ease`;
    cardRef.current.style.removeProperty("--x");
    cardRef.current.style.removeProperty("--y");
    cardRef.current.style.boxShadow = "0 18px 40px rgba(148, 63, 255, 0.25)";
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={
        "relative overflow-hidden rounded-2xl border border-[#e4ddff] bg-white text-slate-900 will-change-transform cursor-pointer " +
        className
      }
    >
      {/* 紫色高光 */}
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(420px circle at var(--x) var(--y), rgba(216,180,254,0.35), transparent 80%)",
          maskImage:
            "radial-gradient(320px circle at var(--x) var(--y), black 40%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(320px circle at var(--x) var(--y), black 40%, transparent 100%)",
          transition: "background 0.2s ease",
        }}
      />
      <div className="relative z-10 h-full">{children}</div>
    </div>
  );
}

// 顶部三大 Pillars（文案与 PPT 一致）
const topPillars = [
  {
    title: "INTELLIGENT",
    subtitle: "DATA CENTER",
    paragraphs: [
      "Our infrastructure empowers enterprises to scale efficiently, offering cutting-edge hardware and accelerating deployment.",
      "Provision of high-performance physical server leasing for cross-border enterprises. These servers are equipped with top-level GPU hardware and global multi-node Tier IV data centers. Server leasing services provide computing power with zero virtualisation loss, meeting the needs of demanding scenarios such as AI training, high-frequency trading, and real-time rendering etc.",
    ],
    icon: "/icon1.png",
    gradient: "from-[#7a3dd9] to-[#9b5bff]",
  },
  {
    title: "ENTERPRISE ENABLEMENT",
    paragraphs: [
      "We design scalable and agile compute, storage, networking, and data architectures to optimize and future-proof infrastructure.",
      "data centre operations and maintenance services with 24/7 proactive monitoring and support, hardware health checks, fault prediction, and security enhancement. AI-driven predictive maintenance and expert-level response ensure continuous high availability of computing resource maximizing asset utilization and supporting uninterrupted business operations.",
    ],
    icon: "/icon2.png",
    gradient: "from-[#e3509b] to-[#ff7a8a]",
  },
  {
    title: "LOW LATENCY CONNECTIVITY",
    paragraphs: [
      "Our scalable, high-performance connectivity solutions meet the complex operational demands of global enterprises.",
      "Holder of Services-Based Operator SBO license in Singapore with comprehensive suite of telecommunication services, utilizing next-generation technology with established infrastructure partnerships to deliver scalable, cost-effective, and high-performance connectivity solutions that meet the stringent demands of global enterprises.",
    ],
    icon: "/icon3.png",
    gradient: "from-[#ff6b79] to-[#ff9460]",
  },
];

// 卡片背景渐变
const topCardGradients = [
  "from-[#f3e7ff] via-[#ffffff] to-[#f5e7ff]", // 左
  "from-[#ffe7f2] via-[#ffffff] to-[#ffe5d6]", // 中
  "from-[#ffe6e6] via-[#ffffff] to-[#ffe6cf]", // 右
];

// 底部网格卡片
const bottomCards = [
  // ENABLE
  {
    category: "ENABLE",
    title: "HIGH PERFORMANCE INFRASTRUCTURE",
    description:
      "GPU-optimised clusters, high-bandwidth fabric, and resilient power & cooling that scale with your AI workloads.",
    icon: GaugeCircle,
    points: [
      "Enterprise-grade GPU & storage building blocks",
      "Dense, energy-efficient racks with DLC options",
      "Designed for predictable performance under load",
    ],
  },
  {
    category: "OPERATE",
    title: "PROACTIVE MONITORING & SUPPORT",
    description:
      "Real-time visibility into clusters, jobs, and utilisation with expert support on standby.",
    icon: Activity,
    points: [
      "24/7 monitoring & alerting across the stack",
      "Health dashboards for GPUs, network, and power",
      "SLO-driven incident response and reporting",
    ],
  },
  {
    category: "CONNECT",
    title: "FIXED & MANAGED CONNECTIVITY",
    description:
      "Dedicated connectivity options that keep your AI workloads close to users and data sources.",
    icon: Zap,
    points: [
      "Low-latency private links and peering",
      "Redundant paths for high availability",
      "QoS tuned for latency-sensitive traffic",
    ],
  },
  {
    category: "ENABLE",
    title: "DATA INTEGRITY & OPTIMIZATION",
    description:
      "Data pipelines and storage layouts tuned for AI training, fine-tuning, and inference.",
    icon: LineChart,
    points: [
      "High-throughput data access patterns",
      "Multi-tier storage for cold / warm / hot data",
      "Governance to keep datasets trusted and compliant",
    ],
  },
  {
    category: "OPERATE",
    title: "SERVICE DELIVERY & SECURITY",
    description:
      "Security and compliance baked into every service layer, from access controls to audit trails.",
    icon: ShieldCheck,
    points: [
      "Strong identity and role-based access",
      "Hardened images and secure baselines",
      "Compliance-ready logging and reporting",
    ],
  },
  {
    category: "CONNECT",
    title: "VPN & EDGE ACCESS",
    description:
      "Secure access from branch sites, remote teams, and edge locations into your AI fabric.",
    icon: Network,
    points: [
      "Managed VPN gateways and policies",
      "Zero-trust aligned access controls",
      "Observability across users and locations",
    ],
  },
  {
    category: "ENABLE",
    title: "HUMAN-CENTERED INNOVATION",
    description:
      "Services and tooling that prioritise usability so teams can focus on delivering business outcomes.",
    icon: Users,
    points: [
      "Bespoke Blueprints for AI projects",
      "Documentation and enablement for your teams",
      "Customised workshops with our specialists",
    ],
  },
  // OPERATE

  {
    category: "OPERATE",
    title: "CONTINUOUS OPTIMIZATION",
    description:
      "Ongoing tuning of capacity, performance, and costs so your AI estate stays efficient.",
    icon: LineChart,
    points: [
      "Capacity planning based on real usage",
      "Workload placement & scheduling guidance",
      "Regular optimisation reviews with our experts",
    ],
  },
  // CONNECT

  {
    category: "CONNECT",
    title: "ECOSYSTEM INTEGRATION",
    description:
      "Pre-integrated connectivity to clouds, data platforms, and tooling you already use.",
    icon: Users,
    points: [
      "Cloud on-ramps and partner exchanges",
      "Data provider and ISV integrations",
      "Reference architectures for hybrid AI",
    ],
  },
];

export default function OurPillars() {
  return (
    // 整个 section 都是浅紫 + 白色渐变，没有深蓝
    <section className="w-full bg-gradient-to-b from-[#f7e6ff] via-[#ffffff] to-[#f9f5ff] text-slate-900">
      {/* 顶部：标题 + 三大 Pillars */}
      <div className="max-w-6xl mx-auto px-4 pt-16 pb-16">
        <div className="text-center mb-14">
          <FadeInOnScroll>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#252666] mb-3">
              EMPOWERING OUR CUSTOMERS
            </h2>
            <p className="text-xs sm:text-sm tracking-[0.25em] text-[#5e4ca8] font-semibold uppercase mb-10">
              OUR CORE PILLARS
            </p>
            <p className="text-[#6b5fa9] text-lg font-medium">
              At AI Nova, every breakthrough in compute is a breakthrough in
              compassion. <br />
              Every model trained helps us understand humanity better.
              <br /> Every partnership we forge builds a bridge to a smarter,
              kinder, more connected world
            </p>
          </FadeInOnScroll>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {topPillars.map((pillar, index) => {
            // const Icon = pillar.icon;
            const cardGradient = topCardGradients[index] || topCardGradients[0];

            return (
              <FadeInCard key={pillar.title} delay={index * 0.2}>
                <div className="flex flex-col items-center h-full">
                  <div className="relative z-10 -mb-10 flex flex-col items-center">
                    {/* 只用你的 logo */}
                    <div className="w-24 h-24 flex items-center justify-center">
                      <img
                        src={pillar.icon}
                        alt={pillar.title}
                        className="w-24 h-24 object-contain"
                      />
                    </div>
                  </div>

                  {/* 主卡片 */}
                  <div
                    className={
                      "w-full min-h-[550px] rounded-[26px] bg-gradient-to-b " +
                      cardGradient +
                      " shadow-[0_20px_45px_rgba(188,148,255,0.35)] border border-[#f3e7ff] pt-14 pb-10 px-8"
                    }
                  >
                    <h3 className="text-sm sm:text-lg  font-extrabold tracking-[0.22em] text-[#18224f] mb-5 text-center">
                      {pillar.title} <br />
                      {pillar.subtitle}
                    </h3>

                    {pillar.paragraphs.map((para, i) => (
                      <p
                        key={i}
                        className={
                          "text-md leading-relaxed text-[#192558]" +
                          (i === 0 ? " mb-4" : "")
                        }
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
              </FadeInCard>
            );
          })}
        </div>
      </div>

      {/* 中间：Enable / Operate / Connect 箭头带（贴近设计稿） */}
      <div className="bg-transparent">
        <FadeInCard delay={0.25}>
          <div className="max-w-6xl mx-auto px-4 mb-14">
            {/* 外层 pill + 阴影 */}
            <div className="relative rounded-md bg-white border border-[#f3edf9] shadow-[0_16px_32px_rgba(0,0,0,0.18)] overflow-hidden">
              {/* 三段纯色底条 */}
              <div className="flex text-[11px] md:text-sm font-semibold tracking-[0.22em] text-white">
                <div className="flex-1 bg-[#6c3ecb] flex items-center justify-center py-3 px-6">
                  <span className="drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                    ENABLE
                  </span>
                </div>
                <div className="flex-1 bg-[#c63d96] flex items-center justify-center py-3 px-6">
                  <span className="drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                    OPERATE
                  </span>
                </div>
                <div className="flex-1 bg-[#ff8a5c] flex items-center justify-center py-3 px-6">
                  <span className="drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                    CONNECT
                  </span>
                </div>
              </div>

              {/* 白色 “>” chevron：覆盖在三段交界处 */}
              <div className="pointer-events-none absolute inset-0">
                {/* ENABLE -> OPERATE */}
                <div className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2 w-9 h-9 bg-white rotate-45 shadow-[0_0_0_2px_rgba(255,255,255,1)]" />
                {/* OPERATE -> CONNECT */}
                <div className="absolute top-1/2 left-2/3 -translate-x-1/2 -translate-y-1/2 w-9 h-9 bg-white rotate-45 shadow-[0_0_0_2px_rgba(255,255,255,1)]" />
              </div>
            </div>
          </div>
        </FadeInCard>
      </div>

      {/* 底部：浅紫背景 + 白色卡片网格 */}
      <div className="bg-transparent pb-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid gap-6 md:grid-cols-3">
            {bottomCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <FadeInCard key={card.title} delay={0.05 * index}>
                  <InteractiveCard className="p-6 min-h-[280px] flex flex-col">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="bg-[#f5f3ff] border border-[#e5ddff] rounded-xl p-2.5 inline-flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#a855f7]" />
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold tracking-[0.22em] text-[#a855f7]">
                          {card.category}
                        </p>
                        <h3 className="mt-1 text-sm md:text-base font-semibold text-slate-900">
                          {card.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-xs md:text-sm text-slate-600 leading-relaxed">
                      {card.description}
                    </p>
                    {card.points && (
                      <ul className="mt-3 space-y-1.5 text-[11px] md:text-xs text-slate-700">
                        {card.points.map((p, i) => (
                          <li
                            key={i}
                            className="relative pl-4 before:content-[''] before:absolute before:left-0 before:top-1.5 before:w-1.5 before:h-1.5 before:rounded-full before:bg-[#a855f7]"
                          >
                            {p}
                          </li>
                        ))}
                      </ul>
                    )}
                  </InteractiveCard>
                </FadeInCard>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
